import { useEffect, useRef, useState } from 'react'
import { api } from '../../api/client'
import { useSession } from '../../api/session'

export default function EmailReconciliation({companyId,jobId,saved}:{companyId:string;jobId:string;saved:()=>Promise<void>}) {
  const { user }=useSession()
  const [open,setOpen]=useState(false)
  const [state,setState]=useState<Awaited<ReturnType<typeof api.emailReconciliation>>|null>(null)
  const [providerId,setProviderId]=useState('')
  const [evidence,setEvidence]=useState('')
  const [error,setError]=useState('')
  const [notice,setNotice]=useState('')
  const [busy,setBusy]=useState(false)
  const [refresh,setRefresh]=useState(0)
  const request=useRef<{body:string;key:string}|null>(null)
  const pending=useRef(false)
  useEffect(()=>{
    if(!open)return
    let live=true
    api.emailReconciliation(companyId,jobId).then(value=>{if(live){setState(value);setError('')}}).catch(e=>{if(live)setError(e.message)})
    return()=>{live=false}
  },[companyId,jobId,open,refresh])
  return <details onToggle={event=>setOpen(event.currentTarget.open)}>
    <summary>Reconcile uncertain email acceptance</summary>
    <p className="muted">Record verified SES acceptance of the archived email below. This does not send or resend an email. Acceptance does not prove delivery to the recipient.</p>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!state && open && !error && <p className="muted">Loading archived email and eligibility…</p>}
    {state && <>
      {!state.eligible && <p className="notice">{state.reason || 'This email is not eligible for acceptance reconciliation.'}</p>}
      {state.recipient && <p><strong>Archived recipient:</strong> {state.recipient}</p>}
      {state.subject && <p><strong>Archived subject:</strong> {state.subject}</p>}
      {state.body && <details><summary>Review archived email body</summary><pre className="compiled-prompt">{state.body}</pre></details>}
      {state.provider_id && <p className="muted">Recorded SES Message ID: {state.provider_id}</p>}
      {state.eligible && user?.role!=='admin' && <p className="muted">An administrator must verify and record email-service acceptance.</p>}
      {state.eligible && user?.role==='admin' && <form className="reply" onSubmit={async event=>{
        event.preventDefault()
        if(pending.current || !providerId.trim() || evidence.trim().length<10)return
        pending.current=true;setBusy(true);setError('');setNotice('')
        try {
          const payload={expected_version:state.version,provider_id:providerId.trim(),evidence:evidence.trim()}
          const body=JSON.stringify(payload)
          if(request.current?.body!==body)request.current={body,key:crypto.randomUUID()}
          const result=await api.reconcileWorkflowEmail(companyId,jobId,{...payload,idempotency_key:request.current.key})
          setState({...state,eligible:false,reason:'SES acceptance has been recorded.',provider_id:result.provider_id})
          setNotice('Verified SES acceptance recorded. No email was resent.')
          await saved()
        }catch(e){setError(e instanceof Error?e.message:'Could not record verified acceptance')}
        finally{pending.current=false;setBusy(false)}
      }}>
        <label className="field">Verified SES Message ID<input className="input" required maxLength={200} disabled={busy} value={providerId} onChange={event=>setProviderId(event.target.value)} /></label>
        <label className="field">Verification evidence<textarea className="input" required minLength={10} maxLength={5000} rows={4} disabled={busy} value={evidence} onChange={event=>setEvidence(event.target.value)} placeholder="Describe the SES record that verifies acceptance for this archived recipient and email." /></label>
        <button className="btn secondary small" disabled={busy || !providerId.trim() || evidence.trim().length<10}>Record verified SES acceptance</button>
      </form>}
    </>}
    {open && <button className="btn secondary small" style={{marginTop:'.75rem'}} disabled={busy} onClick={()=>{setState(null);setError('');setRefresh(value=>value+1)}}>Reload archived email and eligibility</button>}
  </details>
}
